import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconDrinking } from '../../assets/images/hardware-first/behavior-icon-drinking.svg';
import { ReactComponent as IconEating } from '../../assets/images/hardware-first/behavior-icon-eating.svg';
import { ReactComponent as IconLicking } from '../../assets/images/hardware-first/behavior-icon-licking.svg';
import { ReactComponent as GraphDrinking } from '../../assets/images/hardware-first/behavior-graph-drinking.svg';
import { ReactComponent as GraphLicking } from '../../assets/images/hardware-first/behavior-graph-licking.svg';
import { ReactComponent as Chip1 } from '../../assets/images/hardware-first/behavior-chip-1.svg';
import { ReactComponent as Chip2 } from '../../assets/images/hardware-first/behavior-chip-2.svg';
import { ReactComponent as Chip3 } from '../../assets/images/hardware-first/behavior-chip-3.svg';
import { ReactComponent as Chip4 } from '../../assets/images/hardware-first/behavior-chip-4.svg';
import { ReactComponent as Chip5 } from '../../assets/images/hardware-first/behavior-chip-5.svg';
import styles from './HardwareFirstBehaviors.module.scss';

/** The eating card's activity bar: solid feed events on a translucent track, unfilled tail. */
function EatingFillGraph() {
  return (
    <div className={styles.fillGraph}>
      <span className={styles.fillSolid} style={{ left: '40%', width: '5.4%' }} />
      <span className={styles.fillSolid} style={{ left: '62.3%', width: '1.2%' }} />
      <span className={styles.fillSolid} style={{ left: '71.9%', width: '1.2%' }} />
      <span className={styles.fillEmpty} />
    </div>
  );
}

const MORE_BEHAVIOR_CHIPS = [
  { Icon: Chip1, background: 'rgba(154, 88, 254, 0.24)' },
  { Icon: Chip2, background: 'rgba(20, 205, 113, 0.24)' },
  { Icon: Chip3, background: 'rgba(251, 222, 35, 0.24)' },
  { Icon: Chip4, background: 'rgba(144, 69, 19, 0.3)' },
  { Icon: Chip5, background: 'rgba(255, 255, 255, 0.08)' },
];

/**
 * The pick-your-fi page's AI-behavior band, per the figma redesign: dark surface, stacked behavior
 * cards with live-graph artwork, and the more-behaviors chip row. Scoped to the hardware-first
 * page; the standalone PDPs keep their own PlusNewFeatures carousel.
 */
export default function HardwareFirstBehaviors() {
  const { t } = useTranslation();

  const behaviors = [
    {
      Icon: IconDrinking,
      chipBackground: 'rgba(78, 174, 244, 0.28)',
      iconColor: '#4eaef4',
      title: t('drinking'),
      description: t('know_when_its_time_for_a_water_break_even_before_they_start_panting'),
      graph: <GraphDrinking className={styles.lineGraph} preserveAspectRatio="none" />,
    },
    {
      Icon: IconEating,
      chipBackground: 'rgba(255, 164, 17, 0.26)',
      iconColor: '#ffa411',
      title: t('eating'),
      description: t('stay_tuned_to_shifts_in_appetite_and_keep_track_of_when_theyre_being_fed'),
      graph: <EatingFillGraph />,
    },
    {
      Icon: IconLicking,
      chipBackground: 'rgba(229, 16, 59, 0.24)',
      iconColor: '#e5103b',
      title: t('licking'),
      description: t('get_notified_of_unusually_vocal_or_quiet_days_to_address_any_stressors'),
      graph: <GraphLicking className={styles.lineGraph} preserveAspectRatio="none" />,
    },
  ];

  return (
    <section className={styles.band}>
      <div className={styles.bandInner}>
        <div className={styles.copy}>
          <h2 className={styles.heading}>{t('ai_behavior_monitoring')}</h2>
          <div className={styles.subheading}>{t('unleash_a_complete_overview_of_their_habits')}</div>
        </div>
        <div className={styles.cardsColumn}>
          <div className={styles.cards}>
            {behaviors.map((behavior) => (
              <div key={behavior.title} className={styles.card}>
                <div className={styles.cardText}>
                  <div className={styles.cardTitleRow}>
                    <span
                      className={styles.iconChip}
                      style={{ background: behavior.chipBackground, color: behavior.iconColor }}
                    >
                      <behavior.Icon />
                    </span>
                    <span className={styles.cardTitle}>{behavior.title}</span>
                  </div>
                  <div className={styles.cardDescription}>{behavior.description}</div>
                </div>
                <div className={styles.cardGraph}>{behavior.graph}</div>
              </div>
            ))}
          </div>
          <div className={styles.moreBehaviors}>
            <div className={styles.moreTitle}>{t('hardware_first_more_behaviors')}</div>
            <div className={styles.chipRow}>
              {MORE_BEHAVIOR_CHIPS.map(({ Icon, background }, index) => (
                <span key={index} className={styles.moreChip} style={{ background }}>
                  <Icon />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
